import React, { useCallback, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Form, Input, InputNumber, Skeleton } from 'antd'
import { SettingOutlined } from '@ant-design/icons'
import { MdOutlineNotificationsActive, MdOutlineSettings, MdOutlineSpeed, MdOutlineTimer } from 'react-icons/md'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'
import { restClient } from '@/Core/Services/RestClientService'
import { FormActionsBar } from '@/Ship/Components/UI/FormActionsBar'
import { SettingsShell } from '@/Ship/Components/UI/SettingsShell/SettingsShell'
import shell from '@/Ship/Components/UI/SettingsShell/SettingsShell.module.css'
import { cx } from '@/Ship/Helpers/cssClass'
import { notify } from '@/Ship/Helpers/notify'
import {
    DEFAULT_PORTAL_OPTIONS,
    OPTION_DELAY_ESCALATION_MINUTES,
    OPTION_ESCALATION_EMAILS,
    isEqualPortalOptions,
    normalizePortalOptions,
    type PortalOptionsForm,
} from '@/Ship/Helpers/portalOptions'

import { MileageSection } from '../Components/MileageSection'
import { ShiftReminderSection } from '../Components/ShiftReminderSection'

/**
 * Завантажити всі опції з API та перетворити на map code → value
 */
async function fetchOptions(): Promise<Partial<PortalOptionsForm>> {
    const result: any = await restClient.get('/services/tenant/options', {
        searchParams: { limit: 300 },
    })
    const items: any[] = result?.items ?? []
    const map: Partial<PortalOptionsForm> = {}
    items.forEach((item: any) => {
        if (item.code === OPTION_DELAY_ESCALATION_MINUTES) {
            map[OPTION_DELAY_ESCALATION_MINUTES] =
                Number(item.value) || DEFAULT_PORTAL_OPTIONS[OPTION_DELAY_ESCALATION_MINUTES]
        }
        if (item.code === OPTION_ESCALATION_EMAILS) {
            map[OPTION_ESCALATION_EMAILS] = Array.isArray(item.value)
                ? item.value.join(', ')
                : (item.value ?? DEFAULT_PORTAL_OPTIONS[OPTION_ESCALATION_EMAILS])
        }
    })
    return map
}

/**
 * Зберегти одну опцію через POST /services/tenant/options
 */
async function saveOption(code: string, value: unknown): Promise<void> {
    await restClient.post('/services/tenant/options', { json: { code, value } })
}

/** Розділ «Затримки виконання»: час до ескалації та адреси для сповіщень */
const DelaysSection: React.FC = observer(() => {
    const [form] = Form.useForm<PortalOptionsForm>()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    // Базова лінія для «є незбережені зміни»: значення з сервера або після успішного збереження
    const [baseline, setBaseline] = useState<PortalOptionsForm | null>(null)
    // Поточні значення форми через onValuesChange (Form.useWatch на першому рендері
    // віддає порожній об'єкт, і кнопка «Зберегти» блимала активною)
    const [values, setValues] = useState<PortalOptionsForm | null>(null)
    const dirty = baseline !== null && values !== null && !isEqualPortalOptions(values, baseline)

    const loadOptions = useCallback(async () => {
        setLoading(true)
        try {
            const opts = await fetchOptions()
            // Форма монтується вже з initialValues={baseline}, тому setFieldsValue тут не потрібен
            const initial = { ...DEFAULT_PORTAL_OPTIONS, ...opts }
            setBaseline(initial)
            setValues(initial)
        } catch {
            notify.error('Не вдалося завантажити налаштування')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        loadOptions()
    }, [loadOptions])

    const handleReset = () => {
        if (!baseline) return
        form.setFieldsValue(baseline)
        setValues(baseline)
    }

    const handleSave = async (formValues: PortalOptionsForm) => {
        setSaving(true)
        try {
            const normalized = normalizePortalOptions(formValues)
            await Promise.all([
                saveOption(OPTION_DELAY_ESCALATION_MINUTES, normalized[OPTION_DELAY_ESCALATION_MINUTES]),
                saveOption(OPTION_ESCALATION_EMAILS, normalized[OPTION_ESCALATION_EMAILS]),
            ])
            setBaseline(formValues)
            notify.success('Налаштування збережено')
        } catch {
            notify.error('Помилка збереження налаштувань')
        } finally {
            setSaving(false)
        }
    }

    const head = (
        <div className={cx(shell, 'sectionHead')}>
            <h2 className={cx(shell, 'sectionTitle')}>Затримки виконання</h2>
            <p className={cx(shell, 'sectionHint')}>
                Коли затримку не вирішено вчасно, задача ескалюється і про це повідомляються відповідальні.
            </p>
        </div>
    )

    if (loading || !baseline) {
        return (
            <div className={cx(shell, 'section')}>
                {head}
                <Skeleton active paragraph={{ rows: 4 }} />
            </div>
        )
    }

    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={baseline}
            onValuesChange={(_, all) => setValues(all)}
            onFinish={handleSave}
        >
            <div className={cx(shell, 'stack')}>
                <div className={cx(shell, 'section')}>
                    {head}
                    <Form.Item
                        name={OPTION_DELAY_ESCALATION_MINUTES}
                        label="Час до ескалації, хв"
                        rules={[
                            { required: true, message: 'Обовʼязкове поле' },
                            { type: 'number', min: 1, message: 'Значення має бути > 0' },
                        ]}
                        extra="Якщо диспетчер не вирішить затримку протягом цього часу, статус стане «Ескальовано»"
                    >
                        <InputNumber min={1} step={5} precision={0} style={{ width: 200 }} />
                    </Form.Item>

                    <Form.Item
                        name={OPTION_ESCALATION_EMAILS}
                        label="Email-адреси для сповіщень при ескалації"
                        extra="Один або кілька email через кому або з нового рядка. Листи надсилаються автоматично при зміні статусу на «Ескальовано»."
                        style={{ marginBottom: 0 }}
                    >
                        <Input.TextArea rows={3} placeholder="manager@company.ua, director@company.ua" />
                    </Form.Item>
                </div>

                <div className={cx(shell, 'actions')}>
                    <FormActionsBar dirty={dirty} saving={saving} onReset={handleReset} />
                </div>
            </div>
        </Form>
    )
})

/** Сторінка «Налаштування порталу»: оболонка налаштувань, як у профілі користувача */
const IndexPage: React.FC = () => (
    <SettingsShell
        title="Налаштування порталу"
        subtitle="Правила ескалації затримок, сповіщення, контроль пробігу і нагадування водіям"
        avatar={<MdOutlineSettings />}
        sections={[
            {
                key: 'delays',
                label: 'Затримки виконання',
                icon: <MdOutlineTimer />,
                content: <DelaysSection />,
            },
            {
                key: 'mileage',
                label: 'Контроль пробігу',
                icon: <MdOutlineSpeed />,
                content: <MileageSection />,
            },
            {
                key: 'shiftReminder',
                label: 'Нагадування про зміну',
                icon: <MdOutlineNotificationsActive />,
                content: <ShiftReminderSection />,
            },
        ]}
    />
)

export const pageConfig: IPageConfig = {
    key: 'Setting.PortalSettings',
    route: '/settings/portal-settings',
    title: 'Налаштування порталу',
    icon: <SettingOutlined />,
}

export default IndexPage
